import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, valuesOf } from '../skillUsage';

export const perspectiveMarqueeSkill: CodeoSkill = {
    name: SKILL_NAMES.perspectiveMarquee,
    kind: 'codeo',
    description:
        'Run a tilted rail of cards diagonally through the frame so a set reads as a stream with ' +
        'depth — for a body of work, a library, a catalogue, or "hundreds more like this".',
    documentUsesSkill: check(
        (script, markup) =>
            /perspective/.test(markup) &&
            /ease:\s*['"]none/.test(script) &&
            valuesOf(script, 'x').some((value) => Math.abs(value) >= 300),
    ),
    instructions: `# Perspective Marquee

A rail of cards sits at an angle to the frame, tilted in depth, and slides steadily along its own axis. Cards nearer the camera are larger and move faster. It says *volume with quality* — unlike a flat ticker, each card is still individually visible, so the set reads as a body of work rather than as a list.

Use it for a library, a catalogue, a portfolio, or a run of outputs. It is the strongest way to make "there are lots of these, and they are good" land in one frame.

## Markup

Two rails at different depths give the parallax. The whole stage is rotated once; the rails only translate.

\`\`\`html
<div id="marquee-stage" class="overflow-hidden" data-l5-absolute data-l5-allow-offscreen style="position: absolute; inset: -18%; perspective: 1400px;">
  <div id="rail-back" class="flex gap-8" data-l5-allow-offscreen style="position: absolute; left: -30%; top: 22%; rotate: -8deg;">
    <div class="tile-back" style="width: 320px; height: 200px; border-radius: 16px; background: var(--l5-color-1, #1a1e26); opacity: 0.55"></div>
    <div class="tile-back" style="width: 320px; height: 200px; border-radius: 16px; background: var(--l5-color-1, #1a1e26); opacity: 0.55"></div>
    <div class="tile-back" style="width: 320px; height: 200px; border-radius: 16px; background: var(--l5-color-1, #1a1e26); opacity: 0.55"></div>
    <div class="tile-back" style="width: 320px; height: 200px; border-radius: 16px; background: var(--l5-color-1, #1a1e26); opacity: 0.55"></div>
    <div class="tile-back" style="width: 320px; height: 200px; border-radius: 16px; background: var(--l5-color-1, #1a1e26); opacity: 0.55"></div>
    <div class="tile-back" style="width: 320px; height: 200px; border-radius: 16px; background: var(--l5-color-1, #1a1e26); opacity: 0.55"></div>
  </div>
  <div id="rail-front" class="flex gap-11" data-l5-allow-offscreen style="position: absolute; left: -20%; top: 52%; rotate: -8deg;">
    <div class="tile-front" style="width: 460px; height: 290px; border-radius: 22px; background: var(--l5-color-2, #38bdf8)"></div>
    <div class="tile-front" style="width: 460px; height: 290px; border-radius: 22px; background: var(--l5-color-2, #38bdf8)"></div>
    <div class="tile-front" style="width: 460px; height: 290px; border-radius: 22px; background: var(--l5-color-2, #38bdf8)"></div>
    <div class="tile-front" style="width: 460px; height: 290px; border-radius: 22px; background: var(--l5-color-2, #38bdf8)"></div>
    <div class="tile-front" style="width: 460px; height: 290px; border-radius: 22px; background: var(--l5-color-2, #38bdf8)"></div>
  </div>
</div>
\`\`\`

## Timeline code

Both rails travel for the whole scene at constant speed, the front one faster. That speed difference is the depth.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SCENE = 8;

gsap.set('#rail-back', { x: 0 });
gsap.set('#rail-front', { x: 0 });

tl.to('#rail-back', { x: -900, duration: SCENE, ease: 'none' }, 0);
tl.to('#rail-front', { x: -1500, duration: SCENE, ease: 'none' }, 0);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Angle**: −6° to −12°. Steeper reads as a stylistic gimmick and makes the tiles hard to read; flat is just a ticker.
- **Speed ratio**: the front rail should travel 1.5–2× the back one. Equal speeds destroy the depth; more than 3× and the rails read as unrelated.
- **Opposite directions**: running the rails against each other is more dynamic and much busier — only worth it with no copy in the frame.
- **Content on the tiles**: real thumbnails or one-word labels. Anything longer cannot be read as it passes.
- **Headline over the top**: a still headline across the middle works well, as long as it has a scrim or enough contrast against both rails.
- **Flat alternative**: when depth is not wanted and only volume is, use \`${SKILL_NAMES.tickerCrawlStrip}\`.

## Pitfalls

- Rails are \`x\`-tweened, so neither may carry a CSS \`transform\` — use the standalone \`rotate\` property for the tilt.
- Both rails must be wide enough to cover the whole travel distance plus the frame. A rail that runs out leaves a growing empty wedge.
- Everything here reaches past the frame by design, so the stage and both rails need \`data-l5-allow-offscreen\`.
- Never express the travel as an infinite repeat; one long linear tween spanning the scene is the seek-safe version.
- \`ease: 'none'\` is required. Any easing makes the stream appear to speed up or stall.
- Don't animate the tiles individually while the rails move. One motion system.
- A dozen large tiles with shadows is expensive to rasterize every frame; keep them flat fills or simple images.`,
};
